/* ==========================================================================
   ux-review-carousel — the review stepping state.

   Loaded only from sections/ux-review-carousel.liquid.

   The slide is the theme's own: assets/vendor.js bundles Flickity and exposes it
   as window.theme.Flickity — the engine every slideshow on the storefront
   already runs on — so the reviews move the way the rest of the theme moves, a
   real drag with momentum and snapping, wrapping at both ends, and
   `adaptiveHeight`, which is what keeps a section of uneven reviews from being
   as tall as its tallest one. assets/theme.css carries Flickity's base styles
   and marks the reviews that are off-screen `aria-hidden`, so neither is
   restated here.

   This element owns only the section's own furniture: which review is selected
   on the dots (`aria-current`), the carets, the arrow keys — Flickity's own
   keyboard support needs one of its own buttons focused, and the section draws
   none — the teardown, and the one motion switch. `--ux-rc-motion` in the sheet
   is that switch: the box follows the selected review's height on it, and at
   `0ms` (the reduced-motion block) a step also takes Flickity's instant path,
   which is the attraction and the friction, since a slide is physics and has no
   duration.

   Every review is rendered and every one but the first carries `hidden`, so the
   section reads as a single review with no JavaScript at all; those attributes
   come off when the slides are handed to Flickity.
   ========================================================================== */

if (!customElements.get('ux-review-carousel')) {
  customElements.define(
    'ux-review-carousel',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;

        this.controller = new AbortController();
        const { signal } = this.controller;

        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.slidesEl = this.querySelector('.ux-review-carousel__slides');

        const Flickity = window.theme?.Flickity;

        if (this.slides.length < 2 || !this.slidesEl || !Flickity) return;

        this.slides.forEach((slide) => {
          slide.hidden = false;
        });

        this.flkty = new Flickity(this.slidesEl, {
          cellSelector: '[data-slide]',
          adaptiveHeight: true,
          wrapAround: true,
          prevNextButtons: false,
          pageDots: false,
          ...this.instant(),
        });

        /* The box animates only once Flickity has sized it: the viewport is
           created empty, and a transition on the height would otherwise play the
           section open from nothing on load. The gate waits a frame, because a
           transition takes its duration from the *after*-change style — the same
           tick as Flickity's own write would still animate it. */
        requestAnimationFrame(() => this.slidesEl.classList.add('ux-review-carousel__slides--ready'));

        this.flkty.on('select', () => this.select());

        this.querySelectorAll('[data-step]').forEach((step) => {
          step.addEventListener('click', () => this.step(Number(step.dataset.step)), { signal });
        });

        this.dots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.flkty.select(index), { signal });
        });

        this.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'ArrowLeft') this.step(-1);
            else if (event.key === 'ArrowRight') this.step(1);
            else return;

            event.preventDefault();
          },
          { signal }
        );

        this.select();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.flkty?.destroy();
        this.flkty = null;
      }

      step(direction) {
        if (!this.flkty) return;

        if (direction < 0) this.flkty.previous();
        else this.flkty.next();
      }

      select() {
        const index = this.flkty ? this.flkty.selectedIndex : 0;

        this.dots.forEach((dot, dotIndex) => {
          if (dotIndex === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      /* The sheet's own motion token, read back so the sheet stays the one place
         a step is turned off. */
      instant() {
        return parseFloat(getComputedStyle(this).getPropertyValue('--ux-rc-motion')) ? {} : { selectedAttraction: 1, friction: 1 };
      }
    }
  );
}
